import React, { useState, useContext, useCallback, useEffect, useRef } from 'react';
import PropType from 'prop-types';
import RegisterPopup from '@deporvillage/register-popup-component';
import ForgotPasswordPopup from '@deporvillage/forgot-password-popup-component';
import { I18n, Config, getCookie, getCustomerValues, setCustomerValues, getInputError, getFormErrors } from '@deporvillage/utils-library-component';
import { LoginForm, GoogleSocialLogin } from '@deporvillage/login-component';
import Customer from './utils/customer';

import styles from './MyAccount.module.scss';

const Events = {
    ButtonLogin: 'login-button-login',
    LinkSignUp: 'login-link-sign-up',
    LinkForgotPassword: 'login-link-forgot',
    InputEmail: 'login-input-email',
    InputPassword: 'login-input-password',
    GoogleSocialButtonLogin: 'google-social-button-login',
};

const MyAccountLogged = ({ name }) => {

    const { url } = useContext(Config);
    return (
        <div className={ styles['my-account-logged-component'] }>
            <span className={ styles['list-item'] }>
                <I18n args={{ username: name }}>header.actions.hello</I18n>
            </span>
            <a data-testid='my-account-link' className={ styles['list-item'] } href={`${ url }/customer/account`}>
                <I18n>header.actions.my_account_menu</I18n>
            </a>
            <a data-testid='my-account-link' className={ styles['list-item'] } href={`${ url }/customer/account/edit`}>
                <I18n>header.actions.account_info</I18n>
            </a>
            <a data-testid='my-account-link' className={ styles['list-item'] } href={`${ url }/sales/order/history`}>
                <I18n>header.actions.my_orders</I18n>
            </a>
            <a data-testid='my-account-link' className={ styles['list-item'] } href={`${ url }/wishlist`}>
                <I18n>header.actions.my_wishlist_list</I18n>
            </a>
            <a
                data-testid='my-account-link'
                className={ styles['list-item'] }
                href={`${ url }/customer/account/logout`}
            >
                <I18n>header.actions.logout</I18n>
            </a>
        </div>
    );
};

MyAccountLogged.propTypes = {
    name: PropType.string,
};

const MyAccountLoggedOut = ({ onChange, onClick }) => {

    const [customerData, setCustomerData] = React.useState(setCustomerValues(Customer));
    const [loginError, setLoginError] = React.useState(null);
    const [loading, setLoading] = useState(false);
    const [openRegister, setOpenRegister] = React.useState(false);
    const [openForgotPassword, setOpenForgotPassword] = React.useState(false);

    const { api, country } = React.useContext(Config);

    const toggleRegister = () => setOpenRegister(!openRegister);
    const toggleForgotForgotPassword = () => setOpenForgotPassword(!openForgotPassword);

    // istanbul ignore next
    const postCustomerLogin = async (api, payload) => {
        const tokenMd5 = getCookie('token_md5');
        return fetch(`${api}/api/customer/login`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                country: country.code,
                'dpv-session': getCookie('dpv-session'),
                ...(!tokenMd5 ? {} : { 'token-app': tokenMd5 }),
            },
            credentials: 'include',
            body: JSON.stringify(payload),
        })
            .then((ans) => ans)
            .catch((e) => console.log(e));
    };

    // istanbul ignore next
    const handleOnLogin = async (form, e) => {
        onClick && onClick(e);
        const [result, hasErrors] = getFormErrors(form);
        if (hasErrors) {
            setCustomerData(result);
        } else {
            setLoading(true);
            const res = await postCustomerLogin(api, getCustomerValues(customerData));
            /* eslint-disable no-undef */
            if (res.status === 200) {
                window.location.reload();
            } else {
                const error = await res.json();
                setLoading(false);
                setLoginError(error);
            }
        }
    };

    // istanbul ignore next
    const handleOnInputChange = (input, form) => {
        const validateInput = {
            ...form,
            [input.id]: {
                ...input,
                error: getInputError(input, form),
            },
        };
        setCustomerData(validateInput);
        setLoginError(null);
    };

    const handleOnClickSignUp = (e) => {
        e.preventDefault();
        onClick && onClick(Events.LinkSignUp);
        setOpenRegister(!openRegister);
    };

    const handleOnClickSocialLogin = (e) => onClick && onClick(e);
    const handleOnInputFocus = (e) => onChange && onChange(e);
    const handleOnLoginClick = (e) => {
        onClick && onClick(e);
        setOpenForgotPassword(!openForgotPassword);
    };

    return (
        <div className={ styles['my-account-logged-out-component'] }>

                <div className={`${loading ? styles['loading'] : ''}`}>
                    <LoginForm
                        customerData={customerData}
                        onLoginClick={handleOnLoginClick}
                        onChange={handleOnInputChange}
                        onFocus={handleOnInputFocus}
                        onLogin={handleOnLogin}
                        error={loginError}
                    />
                    <div className={styles['social-login-wrapper']}>
                        <p className={styles['title-social']}>
                            <I18n>header.login_from.social_login_or</I18n>
                        </p>
                        <GoogleSocialLogin onSocialLoginClick={handleOnClickSocialLogin} />
                        <p className={styles['new-user-title']}>
                            <I18n>header.login_from.new_user_title</I18n>{' '}
                            <strong className={styles['register']} onClick={handleOnClickSignUp}>
                                <I18n>header.login_from.sign_up_here</I18n>
                            </strong>
                        </p>
                    </div>
                </div>

                <div className={ styles['register-popup-wrapper']}>
                    <RegisterPopup onClose={toggleRegister} isOpen={openRegister} />
                </div>

                <div className={ styles['forgotpassword-popup-wrapper']}>
                    <ForgotPasswordPopup onClose={toggleForgotForgotPassword} isOpen={openForgotPassword} onBack={toggleForgotForgotPassword} />
                </div>
        </div>
    );
};

MyAccountLoggedOut.propTypes = {
    onClick: PropType.func,
    onChange: PropType.func,

};

const MyAccount = ({ name, logged, onClick, onChange }) => {

    const [persistent, setPersitent] = useState(false);
    const wrapperRef = useRef();
    const escAndClickOutside = useCallback(e => {
        if (e.keyCode === 27) {
            setPersitent();
        } else if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
            setPersitent();
        }
    }, []);

    useEffect(() => {
        // eslint-disable-next-line no-undef
        document.addEventListener('mousedown', escAndClickOutside, false);
        // eslint-disable-next-line no-undef
        document.addEventListener('keydown', escAndClickOutside, false);
        return () => {
            // eslint-disable-next-line no-undef
            document.removeEventListener('mousedown', escAndClickOutside, false);
            // eslint-disable-next-line no-undef
            document.removeEventListener('keydown', escAndClickOutside, false);
        };
    }, []);

    return (
        <div className={ styles['my-account-component'] }>
            <div className={`${ styles['user-icon'] } ${ persistent ? styles['persistent-visible'] : '' }`}>
                <span className={ 'icon icon-user' } />
                <span className={ styles['user-icon-text'] } data-testid='user-icon-text'>
                    {name ? name : <I18n>header.actions.my_account</I18n>}
                </span>
            </div>
            <div
                ref={wrapperRef}
                className={`${ styles[`my-account-content`] } ${ !logged ? styles['not-logged'] : '' } ${ persistent ? styles['visible'] : '' }`}
                data-testid='my-account-content'
                onClick={() => setPersitent(true)}
            >
                {logged ? <MyAccountLogged name={name} /> : <MyAccountLoggedOut onClick={ onClick } onChange={ onChange }  />}
            </div>
        </div>
    );
};

MyAccount.propTypes = {
    name: PropType.string,
    logged: PropType.bool,
    onClick: PropType.func,
    onChange: PropType.func,
};

export default MyAccount;
export { Events as MyAccountEvents };
